import { useEffect, useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SidePanel } from '@/admin-area/ui/SidePanel';
import { AddressField } from '@/components/address/address-field';
import type { AddressValue } from '@/components/address/address-value';
import { FormFooter } from '@/components/ui/form-footer';
import { Checkbox } from '@/components/ui/checkbox';
import { ChoiceSelect } from '@/components/ui/choice-select';
import { addressOf, addressUpdateOf, addressValueOf, sameAddress } from '../address';
import { SITE_CATEGORIES } from '../picker/CreateSupplierDialog';
import {
  addSupplierSite,
  type Site,
  type SiteCategory,
  type SiteInput,
  type SitePatch,
  type Supplier,
  updateSite,
  type WriteOutcome,
} from './api';
import { Field, TextField } from './EditSupplierDialog';

type Form = {
  sign: string;
  category: SiteCategory | '';
  address: AddressValue;
  phone: string;
  registeredOffice: boolean;
  active: boolean;
};

function formOf(site: Site | null, supplier: Supplier): Form {
  return {
    sign: site?.sign ?? '',
    category: site?.category ?? '',
    address: addressValueOf(site?.address, supplier.country),
    phone: site?.phone ?? '',
    registeredOffice: site?.registeredOffice ?? false,
    active: site?.active ?? true,
  };
}

const orNull = (value: string): string | null => (value.trim() === '' ? null : value.trim());

/** A new site: only what was filled. */
export function siteInputOf(form: Form): SiteInput {
  const address = addressOf(form.address);
  return {
    sign: orNull(form.sign),
    category: form.category === '' ? null : form.category,
    ...(address ? { address } : {}),
    phone: orNull(form.phone),
    ...(form.registeredOffice ? { registeredOffice: true } : {}),
  };
}

/** A site changed: only what changed; an emptied address is sent with every part null. */
/** `country`, the supplier's: what an address saved without one is taken to be in. */
export function sitePatchOf(site: Site, form: Form, country?: string | null): SitePatch {
  const patch: SitePatch = {};
  if (form.sign.trim() !== (site.sign ?? '').trim()) patch.sign = orNull(form.sign);
  if ((form.category || null) !== site.category) {
    patch.category = form.category === '' ? null : form.category;
  }
  if (!sameAddress(form.address, site.address, country)) {
    patch.address = addressUpdateOf(form.address, site.address);
  }
  if (form.phone.trim() !== (site.phone ?? '').trim()) patch.phone = orNull(form.phone);
  if (form.registeredOffice && !site.registeredOffice) patch.registeredOffice = true;
  if (form.active !== site.active) patch.active = form.active;
  return patch;
}

/**
 * «Aggiungi una sede» and «Modifica la sede» (board 3.1, §3.2.5): sign, type, address (the shared
 * address component, board 2) and phone. Anyone in «Note spese» adds one (`can.addSite`); whoever
 * may edit the registry (`can.edit`) changes one, makes it the registered office (the previous one
 * stays as an ordinary site) or marks it as no longer used. The registered office cannot be
 * unmarked here: another site becomes it instead.
 */
export function SiteDialog({
  open,
  onOpenChange,
  supplier,
  site,
  onDone,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly supplier: Supplier;
  /** The site to change; null adds one. */
  readonly site: Site | null;
  readonly onDone: (outcome: WriteOutcome, done: string) => void;
}) {
  const { t } = useTranslation('suppliers');
  const formId = useId();
  const [form, setForm] = useState<Form>(() => formOf(site, supplier));
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm(formOf(site, supplier));
      setFailed(null);
    }
    // Only on opening: a supplier replaced while the panel is open must not wipe what was typed.
  }, [open, site?.id]);

  const set =
    <K extends keyof Form>(key: K) =>
    (value: Form[K]) =>
      setForm((now) => ({ ...now, [key]: value }));
  const categories = [
    { value: '', label: t('picker.addSite.categoryNone') },
    ...SITE_CATEGORIES.map((value) => ({ value, label: t(`category.${value}`) })),
  ];

  async function submit() {
    if (sending) return;
    let outcome: WriteOutcome;
    if (site === null) {
      setSending(true);
      setFailed(null);
      outcome = await addSupplierSite(supplier.id, siteInputOf(form));
    } else {
      const patch = sitePatchOf(site, form, supplier.country);
      if (Object.keys(patch).length === 0) {
        onOpenChange(false);
        return;
      }
      setSending(true);
      setFailed(null);
      outcome = await updateSite(supplier.id, site.id, patch);
    }
    setSending(false);
    if (outcome.ok) {
      onDone(outcome, t(site === null ? 'siteForm.added' : 'siteForm.saved'));
      return;
    }
    if (outcome.code === 'SUPPLIER_MERGED') {
      setFailed(t('edit.merged'));
    } else if (outcome.status === 403) {
      setFailed(t('siteForm.forbidden'));
    } else if (outcome.status === 422) {
      setFailed(outcome.message ?? t('edit.invalid'));
    } else {
      setFailed(t(site === null ? 'picker.addSite.failed' : 'edit.failed'));
    }
  }

  return (
    <SidePanel
      open={open}
      onOpenChange={onOpenChange}
      title={site === null ? t('siteForm.addTitle') : t('siteForm.editTitle')}
      description={t('edit.description', { name: supplier.name })}
      footer={
        <FormFooter
          className="w-full border-t-0 pt-0"
          form={formId}
          primaryLabel={site === null ? t('picker.addSite.save') : t('edit.submit')}
          primaryDisabled={sending}
          cancelLabel={t('actions.cancel')}
          onCancel={() => onOpenChange(false)}
        />
      }
    >
      <form
        id={formId}
        noValidate
        className="flex flex-col gap-4"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <TextField
          label={t('picker.addSite.sign')}
          value={form.sign}
          max={200}
          help={t('siteForm.signHelp', { name: supplier.name })}
          onChange={set('sign')}
        />
        <Field label={t('picker.addSite.category')} note={t('edit.optionalMasculine')}>
          {(id) => (
            <ChoiceSelect
              id={id}
              value={form.category}
              choices={categories}
              onChange={(next) => set('category')(next as SiteCategory | '')}
            />
          )}
        </Field>
        <AddressField
          label={t('picker.addSite.address')}
          labelNote={t('edit.optionalMasculine')}
          value={form.address}
          onChange={set('address')}
        />
        <TextField
          label={t('data.phone')}
          note={t('edit.optionalMasculine')}
          value={form.phone}
          max={40}
          type="tel"
          help={t('edit.phoneHelp')}
          onChange={set('phone')}
        />
        {supplier.can.edit && !(site?.registeredOffice ?? false) ? (
          <label className="flex min-h-11 cursor-pointer items-start gap-3 text-sm">
            <Checkbox
              className="mt-0.5"
              checked={form.registeredOffice}
              onChange={(event) => set('registeredOffice')(event.target.checked)}
            />
            <span className="flex flex-col gap-0.5">
              <span className="font-semibold">{t('siteForm.registeredOffice')}</span>
              {supplier.registeredOffice ? (
                <span className="text-muted-foreground text-xs">
                  {t('siteForm.registeredOfficeHelp')}
                </span>
              ) : null}
            </span>
          </label>
        ) : null}
        {site !== null && !site.registeredOffice ? (
          <label className="flex min-h-11 cursor-pointer items-center gap-3 text-sm font-semibold">
            <Checkbox
              checked={!form.active}
              onChange={(event) => set('active')(!event.target.checked)}
            />
            {t('siteForm.inactive')}
          </label>
        ) : null}
        {failed ? (
          <p role="alert" className="text-destructive text-sm">
            {failed}
          </p>
        ) : null}
      </form>
    </SidePanel>
  );
}
